<script lang="ts">
	import { createEventDispatcher, onMount } from "svelte";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import AspectRatioPicker from "#lib/components/images/AspectRatioPicker.svelte";
	import ImageError from "#lib/components/images/ImageError.svelte";
	import { matchAspectRatioPreset } from "#lib/components/images/utils.js";
	import { PNG_MIME_TYPE, SVG_MIME_TYPE } from "#lib/image.js";
	import Button from "#lib/ui/Button/index.js";
	import { PUBLIC_CDN_URL, PUBLIC_HOSTNAME } from "$app/env/public";
	import type { CropperCanvas, CropperImage, CropperSelection, CropperShade } from "@cropper/elements";

	interface $$Props {
		/**
		 * The image to be cropped.
		 */
		value: string;
		/**
		 * The content type of the image
		 */
		contentType: string | null | undefined;
		/**
		 * The initial aspect ratio applied when the image is loaded. Setting this value does not lock the aspect ratio of the selection rectangle.
		 */
		initialAspectRatio?: number;
		/**
		 * The aspect ratio to be applied to the selection rectangle. Leave blank for freeform.
		 */
		aspectRatio?: number;
		/**
		 * Informational text to be displayed below the image.
		 */
		caption?: string;
		/**
		 * The desired width of the cropped image.
		 */
		finalWidth?: number | undefined;
		/**
		 * The desired height of the cropped image.
		 */
		finalHeight?: number | undefined;
		/**
		 * The shape to crop the image to. For square images, use "rectangle".
		 * @default "rectangle"
		 */
		shape?: "circle" | "rectangle";
		/**
		 * Bindable. True once the cropper has mounted and the image has loaded. Gate calls to `crop()` on this to avoid a null-ref race.
		 */
		isReady?: boolean;
		/**
		 * Optionally show pre-specified aspect ratio options
		 */
		showAspectRatioPicker?: boolean;
		/**
		 * Start unlocked on Freeform. Still uses initialAspectRatio for the opening crop box
		 * but does not lock resize or select a preset. Only applicable when aspect ratio picker is shown..
		 */
		preferFreeformAspectRatio?: boolean;
	}

	interface $$Events {
		/**
		 * Fired when the image should be converted.
		 */
		convert: CustomEvent<string>;
	}

	const dispatch = createEventDispatcher();
	const CDN_URL = PUBLIC_CDN_URL + "/cdn-cgi/image/";

	export let value: string;
	export let aspectRatio: number | undefined = undefined;
	export let initialAspectRatio: number | undefined = undefined;
	export let caption: string | undefined = undefined;
	export let finalWidth: number | undefined = undefined;
	export let finalHeight: number | undefined = undefined;
	export let shape: "circle" | "rectangle" | undefined = "rectangle";
	export let contentType: string | null | undefined = undefined;
	export let isReady = false;
	export let showAspectRatioPicker = false;
	export let preferFreeformAspectRatio = false;

	let cropperCanvas: CropperCanvas;
	let cropperImage: CropperImage;
	let cropperShade: CropperShade;
	let cropperSelection: CropperSelection;
	let loadError: unknown;
	let originalWidth: number;
	let originalHeight: number;
	let isCropperDisabled = true;
	let hasInitialized = false;

	$: originalURL = getOriginalURL(value);
	$: originalDestination = getOriginURL(value);
	$: selectionInitialAspectRatio = resolveSelectionInitialAspectRatio(preferFreeformAspectRatio, showAspectRatioPicker, initialAspectRatio, aspectRatio);
	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	$: (originalURL, (isReady = false), cropperImage?.classList.add("invisible"));
	$: updateCropperState(contentType === undefined || contentType === SVG_MIME_TYPE);

	function resolveSelectionInitialAspectRatio(preferFreeform: boolean, showPicker: boolean, initial: number | undefined, locked: number | undefined): number {
		if (preferFreeform || (showPicker && !initial && !locked)) {
			return Number.NaN;
		}
		return initial || locked || 1;
	}

	onMount(async () => {
		const { CropperCanvas, CropperImage, CropperShade, CropperHandle, CropperSelection, CropperCrosshair, CropperGrid } = await import("@cropper/elements");

		CropperCanvas.$define();
		CropperImage.$define();
		CropperShade.$define();
		CropperHandle.$define();
		CropperSelection.$define();
		CropperCrosshair.$define();
		CropperGrid.$define();

		try {
			await cropperImage?.$ready(async image => {
				// Set the original width and height of the image
				originalWidth = image.naturalWidth;
				originalHeight = image.naturalHeight;

				// On Safari and Firefox the events fire at different times
				// depending on if the imports above are already cached or not.
				// Neither `await tick()` nor `cropperImage.$nextTick()` work
				// here to defer the seelction until after sizing has occurred.
				setTimeout(() => {
					if (!cropperCanvas || !cropperImage || !cropperSelection || !cropperShade) {
						return;
					}
					applyInitialTranslation();
					setCropperConfig(isCropperDisabled);
					cropperImage.classList.remove("invisible");
					hasInitialized = true;
					isReady = true;
				}, 0);
			});
		} catch (e) {
			loadError = e;
		}
	});

	function updateCropperState(isDisabled: boolean) {
		isCropperDisabled = isDisabled;
		if (!hasInitialized) {
			return;
		}
		setCropperConfig(isDisabled);
	}

	function setCropperConfig(isDisabled: boolean) {
		if (cropperCanvas) {
			cropperCanvas.disabled = isDisabled;
		}
		if (cropperSelection) {
			cropperSelection.hidden = isDisabled;
			if (isDisabled) {
				cropperSelection.$render();
			} else {
				applyInitialSelection(getCropOptions(value));
			}
		}
		if (cropperShade) {
			cropperShade.hidden = isDisabled;
		}
	}

	function getOriginalURL(url: string): string {
		if (url.startsWith(CDN_URL)) {
			const path = url.slice(CDN_URL.length).split("/");
			path.shift();
			return parseOrignalURL(path.join("/"));
		}
		return url;
	}

	function getOriginURL(url: string): string {
		if (url.startsWith(CDN_URL)) {
			const path = url.slice(CDN_URL.length).split("/");
			path.shift();
			return path.join("/");
		}
		return url;
	}

	function getCropOptions(url: string): Record<string, string> {
		if (url.startsWith(CDN_URL)) {
			const path = url.slice(CDN_URL.length).split("/");
			return parseOptions(path.shift() ?? "");
		}
		return {};
	}

	function parseOrignalURL(destination: string): string {
		let originalURL: string = destination;
		if (!destination.startsWith("http://") && !destination.startsWith("https://")) {
			originalURL = `${PUBLIC_CDN_URL}/${destination}`;
		}
		if (destination.startsWith(PUBLIC_HOSTNAME)) {
			originalURL = destination.split(PUBLIC_HOSTNAME)[1];
		}
		return originalURL;
	}

	function parseOptions(options: string): Record<string, string> {
		const res: Record<string, string> = {};
		for (const option of options.split(",")) {
			const [key, value] = option.split("=");
			res[key] = value;
		}
		return res;
	}

	function getMaxSelectionRect(): DOMRect {
		const cropperCanvasRect = cropperCanvas.getBoundingClientRect();
		const cropperImageRect = cropperImage.getBoundingClientRect();
		return new DOMRect(cropperImageRect.left - cropperCanvasRect.left, cropperImageRect.top - cropperCanvasRect.top, cropperImageRect.width, cropperImageRect.height);
	}

	function parseTrim(trim: string): DOMRect {
		const [top, right, bottom, left] = trim.split(";").map(Number.parseFloat);

		const maxSelection = getMaxSelectionRect();
		const matrix = new DOMMatrix(cropperImage.$getTransform());
		const x = maxSelection.x + left * matrix.a;
		const y = maxSelection.y + top * matrix.d;
		const width = maxSelection.width - left * matrix.a - right * matrix.a;
		const height = maxSelection.height - top * matrix.d - bottom * matrix.d;

		return new DOMRect(x, y, width, height);
	}

	function getTrim(): string {
		const maxSelection = getMaxSelectionRect();
		const matrix = new DOMMatrix(cropperImage.$getTransform()).inverse();
		const top = Math.floor((cropperSelection.y - maxSelection.y) * matrix.d);
		const left = Math.floor((cropperSelection.x - maxSelection.x) * matrix.a);
		const bottom = Math.floor((maxSelection.y + maxSelection.height - (cropperSelection.y + cropperSelection.height)) * matrix.d);
		const right = Math.floor((maxSelection.x + maxSelection.width - (cropperSelection.x + cropperSelection.width)) * matrix.a);

		return `${top};${right};${bottom};${left}`;
	}

	function getImageURL(destination: string, options: Record<string, string>): string {
		const optionsString = Object.entries(options)
			.map(([key, value]) => `${key}=${value}`)
			.join(",");
		if (destination.indexOf("http://") === -1 && destination.indexOf("https://") === -1) {
			destination = PUBLIC_HOSTNAME + destination;
		}
		return `${CDN_URL}${optionsString}/${destination}`;
	}

	function applyInitialTranslation() {
		if (!cropperCanvas || !cropperImage) {
			return;
		}
		const matrix = initialTransform();
		cropperImage.$transform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f);
	}

	function applyInitialSelection(options: Record<string, string>) {
		let selection: DOMRect | undefined;
		for (const [key, value] of Object.entries(options)) {
			switch (key) {
				case "w":
				case "width":
					finalWidth = Number.parseInt(value);
					break;
				case "h":
				case "height":
					finalHeight = Number.parseInt(value);
					break;
				case "trim": {
					selection = parseTrim(value);
					break;
				}
			}
		}
		const existingCrop = selection;
		if (!selection) {
			selection = initialSelection();
		}
		if (showAspectRatioPicker) {
			if (preferFreeformAspectRatio) {
				// Keep the 16:9 (or other) opening box from initialSelection, but leave resize unlocked.
				aspectRatio = undefined;
			} else if (aspectRatio !== undefined) {
				aspectRatio = matchAspectRatioPreset(aspectRatio) ?? aspectRatio;
			} else if (existingCrop) {
				aspectRatio = matchAspectRatioPreset(existingCrop.width / existingCrop.height);
			} else {
				aspectRatio = initialAspectRatio;
			}
		}
		cropperSelection.$change?.(selection.x, selection.y, selection.width, selection.height, aspectRatio ?? Number.NaN);
		cropperShade.$change?.(cropperSelection.x, cropperSelection.y, cropperSelection.width, cropperSelection.height);
	}

	function initialTransform(): DOMMatrix {
		const cropperCanvasRect = cropperCanvas.getBoundingClientRect();
		const cropperImageRect = cropperImage.getBoundingClientRect();
		const startX = cropperImageRect.x + cropperImageRect.width / 2;
		const startY = cropperImageRect.y + cropperImageRect.height / 2;
		const endX = cropperCanvasRect.x + cropperCanvasRect.width / 2;
		const endY = cropperCanvasRect.y + cropperCanvasRect.height / 2;
		const scaleX = cropperCanvasRect.width / cropperImageRect.width;
		const scaleY = cropperCanvasRect.height / cropperImageRect.height;

		const dx = endX - startX;
		const dy = endY - startY;

		const [a, b, c, d] = cropperImage.$getTransform();
		const e = (dx * d - c * dy) / (a * d - c * b);
		const f = (dy * a - b * dx) / (a * d - c * b);

		return new DOMMatrix([Math.min(scaleX, scaleY), b, c, Math.min(scaleX, scaleY), e, f]);
	}

	function initialSelection(): DOMRect {
		if (!initialAspectRatio) {
			return getMaxSelectionRect();
		}
		return selectionForAspectRatio(initialAspectRatio);
	}

	function selectionForAspectRatio(ratio: number): DOMRect {
		const maxSelection = getMaxSelectionRect();
		let width = maxSelection.width;
		let height = width / ratio;
		if (height > maxSelection.height) {
			height = maxSelection.height;
			width = height * ratio;
		}
		const left = maxSelection.x + (maxSelection.width - width) / 2;
		const top = maxSelection.y + (maxSelection.height - height) / 2;
		return new DOMRect(left, top, width, height);
	}

	// Explicitly resizes the current selection to the chosen aspect ratio
	// Freeform (no ratio) intentionally leaves the existing crop area untouched
	function handleAspectRatioChange(ratio?: number) {
		if (!ratio || !cropperCanvas || !cropperImage || !cropperSelection || !cropperShade) {
			return;
		}
		const selection = selectionForAspectRatio(ratio);
		cropperSelection.$change?.(selection.x, selection.y, selection.width, selection.height, ratio);
		cropperShade.$change(selection.x, selection.y, selection.width, selection.height);
	}

	function inSelection(selection: DOMRect, maxSelection: DOMRect) {
		return selection.x >= maxSelection.x && selection.y >= maxSelection.y && selection.x + selection.width <= maxSelection.x + maxSelection.width && selection.y + selection.height <= maxSelection.y + maxSelection.height;
	}

	function onSelectionChanged(event: CustomEvent<DOMRect>) {
		const selection = event.detail;
		const maxSelection = getMaxSelectionRect();
		if (!inSelection(selection, maxSelection)) {
			clampSelection(selection, maxSelection);
			cropperSelection.$render();
			event.preventDefault();
		}
		cropperShade.$change(selection.x, selection.y, selection.width, selection.height);
	}

	function clampSelection(selection: DOMRect, maxSelection: DOMRect) {
		selection = clampToBounds(selection, maxSelection);
		if (aspectRatio) {
			selection = clampAspectRatio(selection, aspectRatio);
		}

		cropperSelection.x = selection.x;
		cropperSelection.y = selection.y;
		cropperSelection.width = selection.width;
		cropperSelection.height = selection.height;
	}

	function clampToBounds(selection: DOMRect, maxSelection: DOMRect): DOMRect {
		const isDragging = selection.width === cropperSelection.width && selection.height === cropperSelection.height;
		if (selection.x < maxSelection.x) {
			const offfsetX = maxSelection.x - selection.x;
			selection.x = maxSelection.x;
			if (isDragging) {
				selection.width = cropperSelection.width;
			} else {
				selection.width -= offfsetX;
			}
		}
		if (selection.x + selection.width > maxSelection.x + maxSelection.width) {
			const offsetWidth = selection.x + selection.width - (maxSelection.x + maxSelection.width);
			if (isDragging) {
				selection.width = cropperSelection.width;
			} else {
				selection.width -= offsetWidth;
			}
			selection.x = maxSelection.right - selection.width;
		}
		if (selection.y < maxSelection.y) {
			const offsetY = maxSelection.y - selection.y;
			selection.y = maxSelection.y;
			if (isDragging) {
				selection.height = cropperSelection.height;
			} else {
				selection.height -= offsetY;
			}
		}
		if (selection.y + selection.height > maxSelection.y + maxSelection.height) {
			const offsetHeight = selection.y + selection.height - (maxSelection.y + maxSelection.height);
			if (isDragging) {
				selection.height = cropperSelection.height;
			} else {
				selection.height -= offsetHeight;
			}
			selection.y = maxSelection.bottom - selection.height;
		}
		return selection;
	}

	function clampAspectRatio(selection: DOMRect, aspectRatio: number): DOMRect {
		const newWidth = selection.height * aspectRatio;
		const newHeight = selection.width / aspectRatio;
		if (newWidth > selection.width) {
			const offsetY = (selection.height - newHeight) / 2;
			selection.y += offsetY;
			selection.height = newHeight;
		} else {
			const offsetX = (selection.width - newWidth) / 2;
			selection.x += offsetX;
			selection.width = newWidth;
		}
		return selection;
	}

	export function crop(): string {
		if (!cropperCanvas || !cropperImage || !cropperSelection) {
			return value;
		}
		const options: Record<string, string> = {
			trim: getTrim(),
			f: "auto",
			fit: "contain",
		};
		if (finalWidth) {
			options.w = finalWidth.toString();
		}
		if (finalHeight) {
			options.h = finalHeight.toString();
		}
		return getImageURL(originalDestination, options);
	}

	async function convert() {
		const canvas = await cropperSelection.$toCanvas({ width: originalWidth, height: originalHeight });
		const dataURL = canvas.toDataURL(PNG_MIME_TYPE);
		dispatch("convert", dataURL);
	}
</script>

{#if !loadError}
	<cropper-canvas bind:this={cropperCanvas} background scale-step={0} class="h-full w-full">
		<cropper-image bind:this={cropperImage} crossOrigin="anonymous" src={originalURL} alt="The image to be cropped" initial-center-size="contain" scalable></cropper-image>
		<cropper-shade bind:this={cropperShade} hidden theme-color="rgba(0,0,0,0.4)" class:rounded-full={shape === "circle"}></cropper-shade>
		<cropper-handle action="move" plain></cropper-handle>
		<cropper-selection bind:this={cropperSelection} initial-aspect-ratio={selectionInitialAspectRatio} aspect-ratio={aspectRatio} movable resizable outlined precise on:change={onSelectionChanged} class="outline-2" class:rounded-full={shape === "circle"}>
			<cropper-grid bordered covered theme-color="#fff"></cropper-grid>
			<cropper-crosshair centered></cropper-crosshair>
			<cropper-handle action="move" plain></cropper-handle>
			<cropper-handle action="n-resize"></cropper-handle>
			<cropper-handle action="e-resize"></cropper-handle>
			<cropper-handle action="s-resize"></cropper-handle>
			<cropper-handle action="w-resize"></cropper-handle>
			{#if shape === "rectangle"}
				<cropper-handle action="ne-resize"></cropper-handle>
				<cropper-handle action="nw-resize"></cropper-handle>
				<cropper-handle action="se-resize"></cropper-handle>
				<cropper-handle action="sw-resize"></cropper-handle>
			{/if}
		</cropper-selection>
	</cropper-canvas>
	<div class="min-h-12">
		{#if showAspectRatioPicker}
			<AspectRatioPicker bind:aspectRatio setAspectRatio={handleAspectRatioChange} />
		{/if}
		{#if contentType && isCropperDisabled}
			<div class="flex min-h-12 items-center justify-between">
				<div class="flex items-center gap-2 text-gray-700">
					<Icon name="exclamation-circle" class="h-5 w-5" />
					<P size="xs">This image is an SVG. If you crop it, it will be converted to a PNG.</P>
				</div>
				<Button data-action-name="cropper-convert" color="light" on:click={convert}>Crop image</Button>
			</div>
		{:else if contentType !== undefined && caption}
			<div class="flex min-h-12 items-center gap-2 text-gray-700">
				<Icon name="exclamation-circle" class="h-5 w-5" />
				<P size="xs">{caption}</P>
			</div>
		{/if}
	</div>
{:else}
	<ImageError />
{/if}

<style>
	@reference "#lib/assets/app.css";

	cropper-selection,
	cropper-handle {
		--theme-color: var(--color-primary-500);
	}

	cropper-handle::after {
		width: 10px;
		height: 10px;
	}
</style>
